/**
 * Thin REST client for /commercial/dashboard (boostcore).
 * Uses the existing apiFetch infra (cookie auth, CSRF on mutations, 401 recovery).
 */
import { apiFetch } from '@/auth/apiFetch'
import type { QuoteBillingRow } from '@/stores/useQuoteBillingDashboardStore'

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`${res.status} ${res.statusText}${body ? ` — ${body}` : ''}`)
  }
  return (await res.json()) as T
}

// ── Types ────────────────────────────────────────────────────────────────────

type RenewalStatus = 'overdue' | 'soon' | 'upcoming'

interface DashboardRenewal {
  companyUuid: string
  companyName: string
  quoteUuid: string
  orderType: string
  contractEndDate: string
  totalCa: number
  subscriptionDurationCode: string | null
  renewalStatus: RenewalStatus
  outstandingAmount: number
  isOwner: boolean
}

interface InvoiceAging {
  current: number
  d1_30: number
  d31_60: number
  d61_90: number
  d90plus: number
}

export interface DashboardClient {
  uuid: string
  name: string
  currentStatus: string | null
  totalInvoiced: number
  outstandingAmount: number
  creditNotesAmount: number
  hasOverdueInvoice: boolean
  renewalStatus: RenewalStatus | null
  isOwner: boolean
  // Stats agrégeables par onglet
  aging: InvoiceAging
  marginAmount: number
  marginCa: number
  signedCaPeriod: number
  signedCount: number
  quotesTotal: number
  signedDelayDaysSum: number
}

interface DashboardKpis {
  totalClients: number
  totalInvoiced: number
  totalOutstanding: number
  totalCreditNotes: number
}

export interface DashboardData {
  kpis: DashboardKpis
  renewals: DashboardRenewal[]
  clients: DashboardClient[]
}

export interface TeamMember {
  user: {
    uuid: string
    firstName: string | null
    lastName: string | null
    email: string
    bu: string | null
  }
  stats: DashboardData
}

export interface TeamDashboard {
  members: TeamMember[]
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export function getMyDashboard(): Promise<DashboardData> {
  return apiFetch('/commercial/dashboard/me').then(json<DashboardData>)
}

export function getTeamDashboard(): Promise<TeamDashboard> {
  return apiFetch('/commercial/dashboard/team').then(json<TeamDashboard>)
}

export function getUserDashboard(uuid: string): Promise<{ user: TeamMember['user']; stats: DashboardData }> {
  return apiFetch(`/commercial/dashboard/user/${uuid}`).then(
    json<{ user: TeamMember['user']; stats: DashboardData }>
  )
}

export interface OutstandingInvoice {
  uuid: string
  name: string | null
  invoiceDate: string | null
  invoiceDateDue: string | null
  amountTotal: number | null
  amountResidual: number | null
  paymentState: string | null
  currencyCode: string | null
  isOverdue: boolean
}

export function getCompanyOutstandingInvoices(companyUuid: string): Promise<OutstandingInvoice[]> {
  return apiFetch(`/commercial/dashboard/company/${companyUuid}/outstanding-invoices`).then(
    json<OutstandingInvoice[]>
  )
}

export interface CreditNote {
  uuid: string
  name: string | null
  invoiceDate: string | null
  invoiceDateDue: string | null
  amountTotal: number | null
  amountResidual: number | null
  paymentState: string | null
  currencyCode: string | null
}

export function getCompanyCreditNotes(companyUuid: string): Promise<CreditNote[]> {
  return apiFetch(`/commercial/dashboard/company/${companyUuid}/credit-notes`).then(
    json<CreditNote[]>
  )
}

export interface MyQuoteBillingResult {
  data: QuoteBillingRow[]
  total: number
  page: number
  pageSize: number
}

/** Devis (+ factures rattachées) dont l'utilisateur connecté est l'auteur (salesEngineerUuid). */
export function getMyQuoteBilling(params: { search?: string; status?: string } = {}): Promise<MyQuoteBillingResult> {
  const q = new URLSearchParams()
  if (params.search) q.set('search', params.search)
  if (params.status) q.set('status', params.status)
  const qs = q.toString()
  return apiFetch(`/commercial/dashboard/me/quote-billing${qs ? `?${qs}` : ''}`).then(json<MyQuoteBillingResult>)
}

// ── Sync ─────────────────────────────────────────────────────────────────────

export interface SyncSummary {
  totalPartnersParsed: number
  assignmentsCreated: number
  assignmentsUpdated: number
  assignmentsDeleted: number
  companiesImported: number
  companiesUpdated: number
  missingCompany: number
  missingUser: number
  invoicesCreated: number
  invoicesUpdated: number
  errors: Array<{ odooId?: number; source?: string; message: string }>
}

export type SyncMode = 'quotes' | 'quotes-all' | 'odoo' | 'odoo-me-cleanup' | 'odoo-team' | 'invoices-me'

export function triggerSync(mode: SyncMode = 'quotes'): Promise<SyncSummary> {
  const params =
    mode === 'quotes'          ? '?fromQuotes=true' :
    mode === 'quotes-all'      ? '?fromQuotes=true&allUsers=true' :
    mode === 'odoo-me-cleanup' ? '?fromOdooMeCleanup=true' :
    mode === 'odoo-team'       ? '?fromOdooTeam=true' :
    mode === 'invoices-me'     ? '?invoicesMe=true' :
    '?all=true'
  return apiFetch(`/commercial/dashboard/sync${params}`, { method: 'POST' }).then(json<SyncSummary>)
}
